import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SEO_DEFAULT_DESCRIPTION, SEO_ROUTE_RULES, SEO_SITE_URL, SeoRouteRule } from './seo.config';

/**
 * Mantiene description, robots, canonical y og:url sincronizados con la ruta actual.
 * Google renderiza JS, así que estas etiquetas se leen en cada URL de la SPA.
 */
@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private initialized = false;

  constructor(
    private router: Router,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.apply(event.urlAfterRedirects));
  }

  private apply(url: string): void {
    const path = this.normalizePath(url);
    const rule = this.findRule(path);
    const indexable = rule?.index !== false;
    const description = rule?.description || SEO_DEFAULT_DESCRIPTION;
    const canonical = SEO_SITE_URL + (path === '/' ? '/' : path);

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      name: 'robots',
      content: indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow'
    });
    this.setCanonical(canonical);
  }

  private findRule(path: string): SeoRouteRule | undefined {
    return SEO_ROUTE_RULES.find((rule) => rule.match.test(path));
  }

  /** Quita query string, fragmento y barra final para que la canónica sea única. */
  private normalizePath(url: string): string {
    const path = (url || '/').split(/[?#]/)[0] || '/';
    return path.length > 1 ? path.replace(/\/+$/, '') : '/';
  }

  private setCanonical(href: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
